import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

/** How long to keep looking for the target, and how often. */
const TRIES = 14
const EVERY = 80

/**
 * Scroll to whatever the URL hash names, and mark it once you get there.
 *
 * The other half of the deep-link mechanism. `useHashTab` handles a hash that
 * names a SECTION of a page — it swaps the panel and there is nothing to scroll
 * to, because a tab strip sits at the top. This handles a hash that names a
 * block sitting somewhere down the page: the KPI panel on the dashboard is 1600
 * pixels below the fold, so switching its tab from the command search without
 * moving the viewport lands you on a page that looks unchanged.
 *
 * Two attributes, so a block can answer to more than one name:
 *
 *   data-part="kpi"                        this block IS that destination
 *   data-parts="participation learning"    any of these land here
 *
 * The second is what lets one block own a set of tabs — the hash picks the tab
 * and the same hash scrolls to the panel holding it.
 *
 * It polls rather than looking once. Every page here fetches before it renders,
 * so at the moment the route changes the block usually does not exist yet;
 * fourteen tries over about a second covers the mock latency without leaving a
 * timer running on a page that has no hash at all.
 */
export default function useHashPart() {
  const { hash, pathname, state } = useLocation()

  useEffect(() => {
    /* The page wrote this hash itself, because you clicked one of its tabs —
       see useHashTab. You are looking at the thing already; scrolling it to the
       middle of the viewport would move the page under the click. */
    if (state?.tabWrite) return undefined

    const wanted = decodeURIComponent(hash.slice(1))
    if (!wanted) return undefined

    let tries = 0
    let timer = 0

    const selector = `[data-part="${CSS.escape(wanted)}"], [data-parts~="${CSS.escape(wanted)}"]`

    const look = () => {
      const target = document.querySelector(selector)
      if (target) {
        target.scrollIntoView({ block: 'center', behavior: 'smooth' })
        /* A flash rather than a lasting highlight: it says "this is the thing
           you asked for" and then gets out of the way, which is what a
           permanent ring around a panel never does. */
        target.classList.add('is-part-flash')
        window.setTimeout(() => target.classList.remove('is-part-flash'), 1400)
        return
      }
      tries += 1
      if (tries < TRIES) timer = window.setTimeout(look, EVERY)
    }

    look()
    return () => window.clearTimeout(timer)
  }, [hash, pathname, state])
}
